import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ETransactionType } from "../../helpers/constants";
import useCurrentAccount from "../../hooks/useCurrentAccount";
import CollapsibleBlock from "../CollapsibleBlock/CollapsibleBlock";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../helpers/utils";
import useMainFilter from "../../hooks/useMainFilter";
import userPreferencesStore from "../../store/userPreferences";
import useCollapsibles from "../../hooks/useCollapsibles";
import useAllTransactions from "src/hooks/useAllTransactions";
import Cards from "../Cards/Cards";

interface ITransactionsPendingList {
  columns?: number;
  type?: ETransactionType;
  onlyCurrentAccount?: boolean;
}

const TransactionsPendingList: React.FC<ITransactionsPendingList> = ({
  columns = 1,
  type = ETransactionType.INCOME,
  onlyCurrentAccount = true,
}) => {
  const { accountAllPendingTransactions, currencySymbol } = useCurrentAccount();
  const { allPendingTransactions } = useAllTransactions();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { mainFilter, setMainFilter } = useMainFilter();
  const { setTransactionsActiveTab } = userPreferencesStore();
  const { collapsiblePreferences, updateCollapsible } = useCollapsibles();

  const data = useMemo(() => {
    if (onlyCurrentAccount) {
      return accountAllPendingTransactions?.filter?.(
        (transaction: any) => transaction.type === type
      );
    }

    return allPendingTransactions?.filter?.(
      (transaction: any) => transaction.type === type
    );
  }, [accountAllPendingTransactions, allPendingTransactions, type]);

  const texts = useMemo(() => {
    if (type === ETransactionType.INCOME) {
      return {
        title: t("pending_to_collect_transactions.title"),
        description: t("pending_to_collect_transactions.description", {
          count: data?.length,
        }),
      };
    }

    return {
      title: t("pending_to_pay_transactions.title"),
      description: t("pending_to_pay_transactions.description", {
        count: data?.length,
      }),
    };
  }, [data, type]);

  const totalAmountPending = useMemo(() => {
    return data?.reduce?.((acc: number, curr: any) => {
      return acc + Number(curr?.total);
    }, 0);
  }, [data]);

  if (!data?.length) return null;

  return (
    <CollapsibleBlock
      title={texts.title}
      badge={`${formatCurrency({
        value: totalAmountPending,
        currency: currencySymbol,
      })}`}
      open={
        type === ETransactionType.EXPENSE
          ? collapsiblePreferences.pendingToPayOpen
          : collapsiblePreferences.pendingToCollectOpen
      }
      onChange={(isOpen: boolean) => {
        if (type === ETransactionType.EXPENSE) {
          updateCollapsible({ pendingToPayOpen: isOpen });
        } else {
          updateCollapsible({ pendingToCollectOpen: isOpen });
        }
      }}
      uppercase
    >
      <Cards>
        {data?.map?.((transaction: any, key: number) => (
          <Cards.PendingTransaction
            key={key}
            transaction={transaction}
            index={key}
          />
        ))}
      </Cards>
    </CollapsibleBlock>
  );
};

export default React.memo(TransactionsPendingList);
